import { GAMIFICATION_ROUTES } from "../homeDashboard/gamification/gamificationRoutes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getGamificationBattleClient } from "../homeDashboard/gamification/gamificationBattleClient";
import { buildGamificationBackTarget } from "../homeDashboard/gamification/gamificationFlowState";

const COMPLETED_RESULTS = new Set(["Win", "Lose", "Draw"]);

export function useGamificationAttemptActions(flowState, backPath = GAMIFICATION_ROUTES.start) {
  const navigate = useNavigate();
  const { isAuthenticated, isInitialized, user } = useAuth();
  const client = useMemo(() => getGamificationBattleClient(), []);
  const activeRejoinRef = useRef(0);
  const [socketState, setSocketState] = useState(() => client.state);
  const [rejoiningRoomId, setRejoiningRoomId] = useState(0);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsubscribe = client.subscribe(setSocketState);
    return () => {
      unsubscribe();
      if (!client.hasSubscribers()) client.scheduleIdleDisconnect();
    };
  }, [client]);

  useEffect(() => {
    if (!activeRejoinRef.current || Number(socketState?.battle_room_id) !== activeRejoinRef.current) return;
    if (socketState?.lastSocketAction === "rejoinGame" && socketState?.liveStage === "gameplay" &&
        socketState?.currentQuestion?.question_id) {
      activeRejoinRef.current = 0;
      setRejoiningRoomId(0);
      navigate(GAMIFICATION_ROUTES.liveBattle, { state: socketState });
    } else if (socketState?.socketError && ["rejoinGame", "validationError"].includes(socketState?.lastSocketAction)) {
      activeRejoinRef.current = 0;
      setRejoiningRoomId(0);
      setError(socketState.socketError);
    }
  }, [navigate, socketState]);

  const onAction = useCallback(async (attempt) => {
    const resultKey = String(attempt?.resultKey || "").trim();
    const battlesId = Number(attempt?.battlesId || 0);
    const subjectId = Number(attempt?.subjectId || 0);
    const selectedBattle = {
      battles_id: battlesId,
      battles_name: attempt?.title || attempt?.raw?.battles_name || "Battle",
    };
    const selectedSubject = { subject_id: subjectId };
    setError("");
    if (resultKey === "Ongoing") {
      setNotice({
        title: "Battle Ongoing",
        message: "This battle is still in progress. Please wait until it finishes to see the result.",
      });
      return;
    }
    if (resultKey !== "Rejoin") {
      if (!COMPLETED_RESULTS.has(resultKey)) {
        setNotice({
          title: "Battle Status Unavailable",
          message: "This battle has no completed result yet. Please refresh the activity list later.",
        });
        return;
      }
      navigate(GAMIFICATION_ROUTES.answerSheet, {
        state: {
          ...flowState,
          state_id: Number(flowState?.state_id || flowState?.selectedState?.state_id || 0),
          subject_id: subjectId,
          battle_room_id: Number(attempt?.battleRoomId || 0),
          battles_id: battlesId,
          selectedBattle,
          selectedSubject,
          selectedAttempt: attempt,
          gamificationBackTarget: buildGamificationBackTarget(backPath, flowState),
        },
      });
      return;
    }

    const battleRoomId = Number(attempt?.battleRoomId || 0);
    const userId = Number(user?.user_id || user?.id || 0);
    if (!battleRoomId || !isInitialized || !isAuthenticated || !userId) {
      setError("Sign in and select a valid battle to rejoin.");
      return;
    }
    if (activeRejoinRef.current) return;

    activeRejoinRef.current = battleRoomId;
    setRejoiningRoomId(battleRoomId);
    client.syncFlowState({
      ...flowState,
      battles_id: battlesId,
      subject_id: subjectId,
      selectedBattle,
      selectedSubject,
      battle_room_id: battleRoomId,
      currentQuestion: null,
      liveStage: "rejoining",
      lastSocketAction: "",
      socketError: "",
    });
    const connected = client.state?.isSocketConnected || await client.connect(userId);
    if (activeRejoinRef.current !== battleRoomId) return;
    if (!connected || !client.sendAction("rejoinGame", { battle_room_id: battleRoomId })) {
      activeRejoinRef.current = 0;
      setRejoiningRoomId(0);
      setError(client.state?.socketError || "Unable to connect to the battle.");
    }
  }, [backPath, client, flowState, isAuthenticated, isInitialized, navigate, user?.id, user?.user_id]);

  return {
    onAction,
    notice,
    dismissNotice: () => setNotice(null),
    error,
    rejoiningRoomId,
  };
}
